import type { PublicLanguage } from '@/entities/publicReport';

import { LANGUAGE_LOCALES } from './copy';

/**
 * Getalnotatie op de klantpagina. Nederlands is de norm (`€ 1.450`,
 * `€ 389,00`); kiest de koper een andere taal, dan volgt de notatie die taal.
 */

function normalizeSpaces(value: string): string {
    return value.replace(/[\u00a0\u202f]/g, ' ');
}

export function formatAmount(
    amount: number,
    language: PublicLanguage,
    options: { cents?: boolean } = {},
): string {
    const showCents = options.cents ?? !Number.isInteger(amount);

    return normalizeSpaces(
        new Intl.NumberFormat(LANGUAGE_LOCALES[language], {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: showCents ? 2 : 0,
            maximumFractionDigits: showCents ? 2 : 0,
        }).format(amount),
    );
}

export function formatCount(value: number, language: PublicLanguage): string {
    return normalizeSpaces(new Intl.NumberFormat(LANGUAGE_LOCALES[language]).format(value));
}

/** `3 september 11:02` */
export function formatDateTime(value: string | null, language: PublicLanguage): string | null {
    const date = toDate(value);

    if (!date) {
        return null;
    }

    const locale = LANGUAGE_LOCALES[language];
    const day = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(date);
    const time = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date);

    return normalizeSpaces(`${day} ${time}`);
}

/** `3 september 2027` */
export function formatDate(value: string | null, language: PublicLanguage): string | null {
    const date = toDate(value);

    if (!date) {
        return null;
    }

    return normalizeSpaces(
        new Intl.DateTimeFormat(LANGUAGE_LOCALES[language], {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }).format(date),
    );
}

function toDate(value: string | null): Date | null {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
}

/** Bewust ruim: een adres afwijzen is erger dan een typfout doorlaten. */
export function looksLikeEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

/**
 * `tel:+31118123456` uit `+31 (0)118 12 34 56`: alleen de cijfers, met de plus
 * vooraan als die er stond. De `(0)` na een landnummer valt weg, anders kiest de
 * telefoon een nul te veel. `null` voor een nummer zonder cijfers.
 */
export function phoneHref(phone: string): string | null {
    const number = phone.trim();
    const international = number.startsWith('+');
    const digits = (international ? number.replace(/\(\s*0\s*\)/g, '') : number).replace(/\D/g, '');

    return digits === '' ? null : `tel:${international ? '+' : ''}${digits}`;
}

/** `mailto:info@…` voor iets dat op een e-mailadres lijkt; anders `null`, en staat het er als tekst. */
export function emailHref(email: string): string | null {
    const address = email.trim();

    if (!looksLikeEmail(address)) {
        return null;
    }

    const at = address.lastIndexOf('@');

    return `mailto:${encodeURIComponent(address.slice(0, at))}@${encodeURIComponent(address.slice(at + 1))}`;
}

/**
 * De link achter een website: alleen een absoluut http(s)-adres zonder tekens die
 * uit een attribuut kunnen breken. De API gaf al niets anders; dit is de tweede grendel.
 */
export function websiteHref(url: string): string | null {
    const href = url.trim();

    return /^https?:\/\/[^\s"'<>\\`]+$/i.test(href) ? href : null;
}

/** `keurhuis-voorbeeld.nl` uit `https://www.keurhuis-voorbeeld.nl/`: zonder `https://`, `www.` en slash aan het eind. */
export function websiteLabel(url: string): string {
    return url
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .replace(/\/$/, '');
}

/**
 * De toelichting onder een bevinding op de klantpagina: de eerste alinea, zonder
 * de zin die al als titel boven de kaart staat. Een titel is vaak de eerste zin
 * van dezelfde tekst ("Per direct moeten kozijnen worden geschilderd."); die
 * twee keer lezen helpt niemand. Een ingekorte titel ("…") houdt de hele zin
 * eronder, anders valt het einde weg. Het volledige verhaal staat in de PDF.
 */
export function findingExplanation(title: string, explanation: string): string {
    const [firstParagraph = ''] = explanation.trim().split(/\n\s*\n/);
    const heading = title.trim().toLowerCase();

    if (
        heading === '' ||
        heading.endsWith('…') ||
        !firstParagraph.toLowerCase().startsWith(heading)
    ) {
        return firstParagraph;
    }

    const afterTitle = firstParagraph.slice(heading.length);

    /* De titel is alleen het begin van de eerste zin: dan zegt de zin meer dan de titel. */
    if (!/^[.!?]?(\s|$)/u.test(afterTitle)) {
        return firstParagraph;
    }

    return afterTitle.replace(/^[.!?]?/u, '').trim();
}
